import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { hotResources } from '@/mocks/resources';
import { articles } from '@/mocks/articles';

export default function Home() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typedText, setTypedText] = useState('');
  const placeholderText = '搜索电影、音乐、软件、文档...';

  useEffect(() => {
    let i = 0;
    let increasing = true;
    const interval = setInterval(() => {
      if (increasing) {
        if (i < placeholderText.length) {
          setTypedText(placeholderText.slice(0, i + 1));
          i++;
        } else {
          increasing = false;
          setTimeout(() => {}, 1500);
        }
      } else {
        if (i > 0) {
          setTypedText(placeholderText.slice(0, i - 1));
          i--;
        } else {
          increasing = true;
        }
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/category/movie?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleCategoryClick = (slug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${slug}`);
  };

  const handleArticleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stats = [
    { label: '磁力资源', value: '850万+', icon: 'ri-database-2-line' },
    { label: '日活跃用户', value: '120万+', icon: 'ri-user-smile-line' },
    { label: '日均搜索', value: '500万+', icon: 'ri-search-line' },
    { label: '分类覆盖', value: '8大类', icon: 'ri-stack-line' },
  ];

  return (
    <>
      <SEOHead
        title="黑马磁力 - 强力磁力搜索与资源发现平台"
        description="黑马磁力是专业的磁力链接搜索平台，帮助用户快速发现、检索海量磁力资源。无论您寻找影视、音乐、软件还是文档，黑马磁力都能提供精准高效的磁力搜索体验，让资源获取更简单、更快捷。"
        keywords="黑马磁力,黑马磁力搜索,磁力链接搜索,磁力资源下载,BT磁力搜索引擎,磁力链接大全,最新磁力资源"
        canonicalUrl="https://heimacili.com"
        ogImage="https://readdy.ai/api/search-image?query=Professional%20modern%20dark%20magnetic%20link%20search%20platform%20website%20hero%20with%20gradient%20orange%20teal%20accents%2C%20sleek%20technology%20design%2C%20clean%20digital%20interface%20aesthetic&width=1200&height=630&seq=home-og-001&orientation=landscape"
      />
      <SchemaOrg type="Organization" data={{
        name: '黑马磁力',
        url: 'https://heimacili.com',
        description: '专业的磁力链接搜索与资源发现平台',
        sameAs: ['https://weibo.com/heimacili'],
      }} />
      <SchemaOrg type="WebSite" data={{
        name: '黑马磁力',
        url: 'https://heimacili.com',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://heimacili.com/category/movie?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }} />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20technology%20abstract%20background%20with%20orange%20and%20teal%20glowing%20particles%2C%20data%20streams%2C%20digital%20network%20connections%2C%20futuristic%20cyber%20aesthetic%2C%20clean%20professional%20tech%20design%2C%20dark%20gradient%20with%20warm%20accent%20lighting&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
              alt="黑马磁力背景"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
                黑马磁力 — 强力磁力搜索
              </h1>
              <p className="text-background-50/80 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
                海量磁力资源，精准高效搜索。影视、音乐、软件、文档一网打尽
              </p>

              <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                <div className="flex items-center bg-background-50 rounded-full shadow-lg overflow-hidden">
                  <div className="pl-5 pr-2">
                    <i className="ri-search-line text-xl text-foreground-400"></i>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={typedText}
                    className="flex-1 py-4 text-foreground-800 bg-transparent border-none outline-none text-base placeholder:text-foreground-400"
                  />
                  <button
                    type="submit"
                    className="px-8 py-4 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-base whitespace-nowrap transition-colors cursor-pointer"
                  >
                    搜索资源
                  </button>
                </div>
              </form>

              <div className="flex items-center justify-center gap-4 mt-6 flex-wrap">
                <span className="text-background-50/60 text-sm">热门搜索：</span>
                {['流浪地球3', '无损音乐FLAC', '黑神话悟空', 'Python教程'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => { setSearchTerm(tag); }}
                    className="px-3 py-1.5 rounded-full bg-background-50/15 text-background-50/80 text-sm hover:bg-background-50/25 transition-colors border border-background-50/20 whitespace-nowrap cursor-pointer"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 border-2 border-background-50/40 rounded-full flex items-start justify-center">
              <div className="w-1.5 h-3 bg-background-50/50 rounded-full mt-1.5 animate-bounce"></div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-10 z-20 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-background-50 p-5 rounded-lg text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${stat.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 pt-16 md:pt-20 pb-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">分类浏览</h2>
              <p className="text-foreground-500 mt-2 text-sm">按类别快速找到你需要的磁力资源</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className="group p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-all cursor-pointer text-left"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 group-hover:bg-primary-100 transition-colors mb-3">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Resources */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门资源
                </h2>
                <p className="text-foreground-500 text-sm mt-1">当前最受欢迎的磁力资源，种子数实时更新</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-background-200/70 text-xs font-medium text-foreground-600">
                <div className="col-span-5">资源名称</div>
                <div className="col-span-2">大小</div>
                <div className="col-span-1">日期</div>
                <div className="col-span-2 text-center">种子/下载</div>
                <div className="col-span-2 text-right">操作</div>
              </div>
              {hotResources.map((res, idx) => (
                <div
                  key={res.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-background-200 last:border-b-0 items-center hover:bg-background-50/50 transition-colors"
                >
                  <div className="col-span-5 flex items-center gap-3">
                    <span className="text-xs font-bold text-primary-500 w-5 flex-shrink-0 hidden md:inline">{idx + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground-800 truncate">{res.title}</div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {res.tags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-accent-100 text-accent-700 font-medium">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 text-sm text-foreground-600 hidden md:block">{res.size}</div>
                  <div className="col-span-1 text-sm text-foreground-500 hidden md:block">{res.date}</div>
                  <div className="col-span-2 text-center hidden md:block">
                    <span className="text-sm text-accent-600 font-medium">{res.seeds.toLocaleString()}</span>
                    <span className="text-xs text-foreground-400"> / {res.leeches.toLocaleString()}</span>
                  </div>
                  <div className="col-span-2 text-right flex items-center justify-end gap-2">
                    <span className="text-xs text-foreground-500 md:hidden">{res.size} · {res.date}</span>
                    <button className="px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-link mr-1"></i>复制链接
                    </button>
                    <button className="px-3 py-1.5 rounded-md bg-accent-50 text-accent-700 text-xs font-medium hover:bg-accent-100 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line mr-1"></i>下载
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">最新文章</h2>
              <p className="text-foreground-500 mt-2 text-sm">磁力搜索技巧、资源推荐、使用指南</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  onClick={handleArticleClick}
                  className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700 font-medium">{tag}</span>
                      ))}
                      <span className="text-xs text-foreground-400 ml-auto">{article.date}</span>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 hover:border-primary-500 hover:text-primary-600 transition-colors text-sm font-medium"
              >
                浏览更多资源 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">常见问题</h2>
              <p className="text-foreground-500 mt-2 text-sm">关于磁力搜索的常见疑问解答</p>
            </div>
            <div className="space-y-4">
              {[
                { q: '什么是磁力链接？', a: '磁力链接（Magnet Link）是一种P2P文件标识方式，它通过文件内容的哈希值来唯一标识文件，不依赖中心化服务器即可实现文件定位和下载。' },
                { q: '如何使用磁力链接下载资源？', a: '复制磁力链接后，在支持磁力链接的下载工具（如qBittorrent、BitComet等）中粘贴即可开始下载。下载速度取决于资源的种子数量和您的网络环境。' },
                { q: '黑马磁力搜索是否收费？', a: '黑马磁力完全免费使用，我们致力于为用户提供便捷、高效的磁力链接搜索服务。所有搜索功能均不收取任何费用。' },
                { q: '搜索结果中的种子数和下载者数是什么意思？', a: '种子数（Seeds）表示拥有完整文件并正在上传的用户数，下载者数（Leeches）表示正在下载但尚未拥有完整文件的用户数。种子数越多，下载速度通常越快。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 rounded-lg border border-background-200 group">
                  <summary className="px-5 py-4 cursor-pointer font-medium text-foreground-800 flex items-center justify-between text-sm">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 md:px-6 py-16 md:py-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20abstract%20technology%20background%20with%20warm%20orange%20gradient%20overlay%2C%20subtle%20geometric%20patterns%2C%20minimalist%20modern%20design%2C%20professional%20corporate%20aesthetic&width=1920&height=600&seq=cta-bg-001&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">立即开始搜索你想要的资源</h2>
            <p className="text-background-50/70 text-sm mb-8">加入黑马磁力，畅享海量磁力资源的高效搜索体验</p>
            <form onSubmit={handleSearch} className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="输入关键词开始搜索..."
                className="flex-1 px-5 py-3.5 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 placeholder:text-background-50/50 outline-none text-sm"
              />
              <button
                type="submit"
                className="px-8 py-3.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm whitespace-nowrap transition-colors cursor-pointer"
              >
                开始搜索
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}